import { Emitter, Event } from "../../../base/common/events.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";

/**
 * Context key that can be used in when clauses.
 * Examples: 'treeViewFocus', 'textInputFocus', 'modalOpen'
 */
export type ContextKey = string;

/**
 * Context value - typically boolean but can be string or number for richer contexts
 */
export type ContextValue = boolean | string | number;

/**
 * Service that tracks application context state for conditional keybindings and UI behavior.
 *
 * Context examples:
 * - treeViewFocus: true when a tree node has focus
 * - textInputFocus: true when any text input is focused
 * - modalOpen: true when a modal dialog is visible
 */
export interface IContextService {
	/**
	 * Event fired when context changes
	 */
	readonly onDidChangeContext: Event<{ key: ContextKey; value: ContextValue | undefined }>;

	/**
	 * Set a context key to a value
	 */
	setContext(key: ContextKey, value: ContextValue): void;

	/**
	 * Remove a context key
	 */
	removeContext(key: ContextKey): void;

	/**
	 * Get the current value of a context key
	 */
	getContext(key: ContextKey): ContextValue | undefined;

	/**
	 * Check if a context key is set to a truthy value
	 */
	hasContext(key: ContextKey): boolean;

	/**
	 * Evaluate a when clause expression
	 * Examples:
	 * - "treeViewFocus" -> true if treeViewFocus is truthy
	 * - "!textInputFocus" -> true if textInputFocus is falsy
	 * - "treeViewFocus && !textInputFocus" -> both conditions must be true
	 */
	evaluate(whenClause: string): boolean;
}

export const IContextService = createDecorator<IContextService>('contextService');

export class ContextService extends Disposable implements IContextService {
	declare readonly _serviceBrand: undefined;
	private readonly _onDidChangeContext = this._register(new Emitter<{ key: ContextKey; value: ContextValue | undefined }>());
	readonly onDidChangeContext = this._onDidChangeContext.event;

	private readonly contexts = new Map<ContextKey, ContextValue>();

	setContext(key: ContextKey, value: ContextValue): void {
		const oldValue = this.contexts.get(key);
		if (oldValue === value) {
			return; // No change
		}

		this.contexts.set(key, value);
		this._onDidChangeContext.fire({ key, value });
	}

	removeContext(key: ContextKey): void {
		if (!this.contexts.has(key)) {
			return;
		}

		this.contexts.delete(key);
		this._onDidChangeContext.fire({ key, value: undefined });
	}

	getContext(key: ContextKey): ContextValue | undefined {
		return this.contexts.get(key);
	}

	hasContext(key: ContextKey): boolean {
		const value = this.contexts.get(key);
		return value !== undefined && value !== false && value !== 0;
	}

	evaluate(whenClause: string): boolean {
		if (!whenClause || whenClause.trim() === '') {
			return true; // Empty when clause is always true
		}

		// Simple expression evaluator
		// Supports: "key", "!key", "key && key2", "key || key2"
		const trimmed = whenClause.trim();

		// Handle OR operator (||)
		if (trimmed.includes('||')) {
			const parts = trimmed.split('||').map(p => p.trim());
			return parts.some(part => this.evaluate(part));
		}

		// Handle AND operator (&&)
		if (trimmed.includes('&&')) {
			const parts = trimmed.split('&&').map(p => p.trim());
			return parts.every(part => this.evaluate(part));
		}

		// Handle negation (!)
		if (trimmed.startsWith('!')) {
			const key = trimmed.slice(1).trim();
			return !this.hasContext(key);
		}

		// Simple key lookup
		return this.hasContext(trimmed);
	}
}

/**
 * Scoped context setter that automatically removes context when disposed.
 * Useful for setting context in UI components that need cleanup.
 */
export class ScopedContextKey implements IDisposable {
	constructor(
		private readonly contextService: IContextService,
		private readonly key: ContextKey,
		private readonly value: ContextValue
	) {
		this.contextService.setContext(key, value);
	}

	dispose(): void {
		this.contextService.removeContext(this.key);
	}
}

registerSingleton(IContextService, new SyncDescriptor(ContextService));
